import { html, nothing } from 'lit';
import { property, query, queryAssignedElements } from 'lit/decorators.js';
import { style } from './zui-shell-topbar-css.js';
import { ZuiShellBase } from './zui-shell-base';
import { classMap } from 'lit/directives/class-map.js';
import { pageScrollChangeEventName } from './utils/shell-state';
import '@zywave/zui-icons';
import type { PropertyValues } from 'lit';

const HIDE_CLASS_NAME = 'hide';
const PREFERS_REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * There are two parts to the topbar: global and app. The top white bar is our global topbar, which has Zywave branding and includes global functionality. The bottom colored bar is our app bar (aka branding bar), which is tailored to the application or Cloud the user is viewing. Sometimes it is branded with the product name if not part of a Cloud. Global search, user information `<zui-shell-user>`, and the apps launchpad `<zui-shell-apps>` are all housed in this element.
 *
 * @element zui-shell-topbar
 *
 * @slot - Default, unnamed slot; not recommended for use in `<zui-shell-topbar>`. Please use one of the assigned slots for all purposes!
 * @slot apps - For slotting in the `<zui-shell-apps>` element; should not be used with the `logo` slot
 * @slot logo - For slotting in a logo if not slotting in `<zui-shell-apps>`; should not be used with the `apps` slot because it has its own way to handle logos
 * @slot search - For slotting in a search component such as `<zui-search>`
 * @slot user - For slotting in `<zui-shell-user>`
 * @slot help - For slotting in `<zui-shell-help>`
 *
 * @cssprop [--zui-shell-topbar-app-opacity=0] - App bar opacity for controlling the visiblity of the current application's name when set for anchor solutions; only applicable to anchor solutions and Client Cloud
 * @cssprop [--zui-shell-topbar-app-height = (variable)] - Height of the `<zui-shell-topbar>` app bar (colored bar) for anchor solutions; only applicable to anchor solutions and Client Cloud
 * @cssprop [--zui-shell-topbar-app-height-collapsed=5px] - Collapsed height of the `<zui-shell-topbar>` app bar (colored bar) for anchor solutions when topbar scrolling animation is enabled; only applicable to anchor solutions and Client Cloud
 * @cssprop [--zui-shell-topbar-global-color=#fff] - Background color of the `<zui-shell-topbar>` global topbar
 * @cssprop [--zui-shell-topbar-global-height=(variable)] - Height of the `<zui-shell-topbar>` global topbar
 *
 * @csspart app-bar - Exposes the thin accent bar for styling purposes; this is exposed as a CSS shadow part and can be accessed with `::part(app-bar)`
 *
 * @event {CustomEvent} topbarheightchange - Fired when user scrolls down from the top of the page, or scrolls up when not at the top of the page
 * @event {CustomEvent} pageScrollChangeEventName - Fired on scroll, to correctly size topbar
 */
export class ZuiShellTopBar extends ZuiShellBase {
  /**
   * (optional): To hide the app bar and only show the global topbar; mainly for anchor solutions and Client Cloud
   */
  @property({ type: Boolean, attribute: 'no-appbar' }) noAppBar = false;

  /**
   * (optional): To opt out of the topbar's off-screen scrolling animation.
   */
  @property({ type: Boolean, attribute: 'no-scroll-animation' }) noScrollAnimation = false;

  /**
   * Indicates whether global search is available and open on mobile
   */
  @property({ type: Boolean, reflect: true, attribute: 'search-open' }) searchOpen = false;

  /**
   * Query topbar wrapper to show and hide based on window scroll
   */
  @query('#topbar-wrapper') _topbarWrapper: HTMLElement;

  /**
   * Query elements assigned to the search slot.
   * For search components only
   */

  @queryAssignedElements({ slot: 'search' })
  _slottedSearchEls: HTMLElement[];

  /**
   * Query elements assigned to the default slot
   * Backwards compatibility for search components only
   */

  @queryAssignedElements({ selector: '#zui-shell-topbar-default-slot' })
  _slottedDefaultEls: HTMLElement[];

  #windowScrollY: number | undefined = undefined;
  #currentScrollY: number = 0;
  #lastKnownScrollY: number = 0;
  #ticking: boolean = false;
  #showMobileSearchButton: boolean = false;

  static get styles() {
    return [super.styles, style];
  }

  constructor() {
    super();

    this._observeAppNameChange = true;
    this._observeHasShellNav = true;
    this._observeBannerHeightChange = true;
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.noAppBar) {
      this._hasAppBar = false;
    }

    if (!this.noScrollAnimation && !PREFERS_REDUCED_MOTION) {
      window.addEventListener('scroll', this.#onPageScroll.bind(this));
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('scroll', this.#onPageScroll.bind(this));
  }

  firstUpdated(changedProps: PropertyValues) {
    super.firstUpdated(changedProps);
    this.setAttribute('role', 'banner');

    if (this._isMobile) {
      this.requestUpdate();
    }

    this.#dispatchScrollChangeEvent();
  }

  // Hide mobile search when not in mobile view
  protected onScreenBreakpointChange() {
    super.onScreenBreakpointChange();
    if (this.searchOpen && !this._isMobile) {
      this.searchOpen = false;
    }
    this.#dispatchScrollChangeEvent();
  }

  render() {
    return html`
      <div id="topbar-wrapper" class="topbar-wrapper">
        <div class="close-search" @click="${() => (this.searchOpen = !this.searchOpen)}"></div>
        <div class="global">
          ${this._hasShellNav
            ? html`
                <button class="menu" aria-label="Toggle nav menu" @click="${this.#toggleMobileMenu}">
                  <zui-icon icon="zui-shell-menu"></zui-icon>
                </button>
              `
            : ''}
          <div class="logo"> <slot name="apps"></slot><slot name="logo"></slot> </div>
          <div class="search"> ${this.#renderSearch()} </div>
          <div class="pin-right">
            ${this.#renderMobileSearchToggleButton()}<slot name="help"></slot> <slot name="user"></slot
          ></div>
        </div>
        ${this.noAppBar
          ? ''
          : html`
              <div
                class="app ${classMap({
                  'no-app-name': !this._appName,
                })}"
                part="app-bar"
              >
                <div>${this._isMobile ? nothing : html` <slot name="app">${this._appName}</slot> `}</div>
              </div>
            `}
      </div>
    `;
  }

  // If slotted search element exists, switch views on mobile
  #renderSearch() {
    if (this._isMobile) {
      if (this._slottedDefaultEls.length > 0 || this._slottedSearchEls.length > 0) {
        return html`
          <div class="search-field">
            <slot id="zui-shell-topbar-default-slot" @slotchange="${this.#determineMobileSearchVisibility}"></slot>
            <slot name="search" @slotchange="${this.#determineMobileSearchVisibility}"></slot>
            <zui-button @click="${() => (this.searchOpen = !this.searchOpen)}">Cancel</zui-button>
          </div>
        `;
      } else {
        return html`
          <slot id="zui-shell-topbar-default-slot" @slotchange="${this.#determineMobileSearchVisibility}"></slot>
          <slot name="search" @slotchange="${this.#determineMobileSearchVisibility}"></slot>
        `;
      }
    } else {
      return html`
        <slot id="zui-shell-topbar-default-slot" @slotchange="${this.#determineMobileSearchVisibility}"></slot>
        <slot name="search" @slotchange="${this.#determineMobileSearchVisibility}"></slot>
      `;
    }
  }

  // If search is available, show the search icon button in the topbar to toggle search view
  #renderMobileSearchToggleButton() {
    if (this.#showMobileSearchButton) {
      return html`<button class="search-button" @click="${this.#toggleSearchView}">
        <zui-icon icon="zui-search"></zui-icon>
      </button>`;
    }
  }

  // If ZUI Shell has a side nav and is in mobile view, toggle the mobile menu
  #toggleMobileMenu() {
    if (this._hasShellNav) {
      this._isMobileNavOpen = !this._isMobileNavOpen;
    }
  }

  // If the search slot or default slots have elements in them, only toggle search for mobile
  #toggleSearchView() {
    this.searchOpen = !this.searchOpen;

    const focusOnSearchSlot = (slottedElement: HTMLElement) => {
      let searchElement: HTMLElement | undefined = undefined;
      if (slottedElement.tagName === 'ZUI-SEARCH' || slottedElement.tagName === 'INPUT') {
        searchElement = slottedElement;
      } else {
        searchElement = slottedElement.querySelector('zui-search,input') as HTMLElement;
      }
      if (searchElement) {
        searchElement.focus();
      }
    };

    // If in mobile view, focus on search element and hide submit button
    if (this._isMobile) {
      if (this._slottedSearchEls.length > 0) {
        focusOnSearchSlot(this._slottedSearchEls[0]);
      } else if (this._slottedDefaultEls.length > 0) {
        focusOnSearchSlot(this._slottedDefaultEls[0]);
      }
    }

    // When zui-search searches, close search view
    this.addEventListener('search', () => {
      this.searchOpen = false;
    });
  }

  #onPageScroll() {
    this.#windowScrollY = Math.round(window.scrollY);

    if (this.#windowScrollY === 0) {
      this.#updateScrollPosition();
    }
    if (this.#windowScrollY >= 50) {
      this.#currentScrollY = window.scrollY;
      this.#requestTick();
    }
  }

  // Determine if mobile search icon button should be shown based on the presence of search elements
  async #determineMobileSearchVisibility() {
    const slottedSearch = await this._slottedSearchEls;
    const slottedDefaultSearch = await this._slottedDefaultEls;
    if (this._isMobile && (slottedSearch.length > 0 || slottedDefaultSearch.length > 0)) {
      this.#showMobileSearchButton = true;
    } else {
      this.#showMobileSearchButton = false;
    }
    this.requestUpdate();
  }

  #requestTick() {
    if (!this.#ticking) {
      requestAnimationFrame(this.#updateScrollPosition.bind(this));
    }
    this.#ticking = true;
  }

  #showTopbar() {
    this._topbarWrapper.classList.toggle(HIDE_CLASS_NAME, false);
    this._state.pageScroll = false;
  }

  #hideTopbar() {
    this._topbarWrapper.classList.toggle(HIDE_CLASS_NAME, true);
    this._state.pageScroll = true;
  }

  #updateScrollPosition() {
    if (this.#windowScrollY === 0) {
      this.#showTopbar();
    } else {
      // intentional nesting of if-statements to record current and last scroll positions correctly
      if (this.#currentScrollY < this.#lastKnownScrollY) {
        if (this._state.pageScroll) {
          this.#showTopbar();
        }
      } else if (this.#currentScrollY > 1) {
        if (!this._state.pageScroll && !this._isMobile) {
          this.#hideTopbar();
        }
      }
    }

    this.#lastKnownScrollY = this.#currentScrollY <= 0 ? 0 : this.#currentScrollY;
    this.#ticking = false;
    this.#dispatchScrollChangeEvent();
  }

  #dispatchScrollChangeEvent() {
    const style: CSSStyleDeclaration = window.getComputedStyle(this._topbarWrapper);
    const height: number =
      this._state.pageScroll && !this._isMobile
        ? parseFloat(style.getPropertyValue('--zui-shell-topbar-app-height-collapsed'))
        : parseFloat(style.getPropertyValue('--zui-shell-topbar-global-height')) +
          (!this._state.privateAppName
            ? parseFloat(style.getPropertyValue('--zui-shell-topbar-app-height-collapsed'))
            : parseFloat(style.getPropertyValue('--zui-shell-topbar-app-height')));
    const cssHeight: string = `${height}rem`;
    const topbarVisible: boolean = !this._topbarWrapper.classList.contains(HIDE_CLASS_NAME);

    this.dispatchEvent(
      new CustomEvent(pageScrollChangeEventName, {
        detail: { height: cssHeight, topbarVisible: topbarVisible },
        bubbles: true,
        composed: true,
      })
    );

    this._state.topBarHeight = cssHeight;
  }
}

window.customElements.define('zui-shell-topbar', ZuiShellTopBar);

declare global {
  interface HTMLElementTagNameMap {
    'zui-shell-topbar': ZuiShellTopBar;
  }
}
